import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CookiesService } from '@services/cookies.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MakersiteService } from '@services/makersite.service';
import { ToastrService } from '@services/toaster.service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, retry, switchMap, take } from 'rxjs/operators';

export const SKIP_ERROR_SNACKBAR = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private toastrService: ToastrService,
    private localStorageService: LocalStorageService,
    private cookiesService: CookiesService,
    private dialog: MatDialog,
    private makersiteService: MakersiteService
  ) {}

  loginRedirectMessages = [
    'Invalid token',
    'Invalid token due to logout',
    'Error validating token - please reset your password and try again',
    'You are not a member of this team - please ask your team lead to give you access.',
    'Token validation failed: Signature has expired',
    'EXPIRED TOKEN - Signature has expired',
    'Unable to log in for an unknown reason. Please contact support.'
  ];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => {
        if (error.url.includes('/public/heartbeat')) return EMPTY;
        const skipError = request.context.get(SKIP_ERROR_SNACKBAR);

        if (
          !skipError &&
          error?.error?.message &&
          !this.isLoginRedirectMessage(error?.error?.message) &&
          !request.url.includes('/info/check_availability')
        ) {
          this.toastrService.showMessage(error.error.message, 2000);
        }

        if (
          error.status === 401 &&
          this.isLoginRedirectMessage(error?.error?.message) &&
          !this.router.url.includes('login')
        ) {
          const currentUrl = this.router.url;
          return this.makersiteService.getNewAccessToken().pipe(
            take(1),
            switchMap((newToken: { token: string; refresh_token: string }) => {
              if (newToken && newToken.token) {
                localStorage.setItem('auth_token', newToken.token);
                localStorage.setItem('refresh_token', newToken.refresh_token);

                const clonedRequest = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken.token}`
                  }
                });

                return next.handle(clonedRequest);
              }
              this.redirectToLogin(currentUrl);
              return throwError(() => error);
            }),
            catchError((refreshTokenError) => {
              this.redirectToLogin(currentUrl);
              return throwError(() => refreshTokenError);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private clear() {
    this.localStorageService.clear();
    sessionStorage.clear();
    this.cookiesService.clearCookies();
    this.dialog?.closeAll();
  }

  private isLoginRedirectMessage(message: string | undefined): boolean {
    if (!message) return false;
    const lowerMessage = message.toLowerCase();
    return this.loginRedirectMessages.some((m) => m.toLowerCase() === lowerMessage);
  }

  private redirectToLogin(returnUrl: string) {
    this.toastrService.showMessage('Your session has expired, please log in again.', 5000);
    this.clear();
    this.router.navigate(['login'], {
      queryParams: { next: returnUrl.replace('/', '') }
    });
  }
}
